import { supabase } from '../lib/supabaseClient';

// Property Management Services for Supabase Integration

// Helper: Convert empty strings to null for numeric and constrained text fields
function cleanPropertyData(data) {
  const nullableFields = ['price_amount', 'area', 'bedrooms', 'bathrooms', 'parking', 'year_built', 'latitude', 'longitude', 'price_period'];
  const cleaned = { ...data };

  nullableFields.forEach(field => {
    if (cleaned[field] === '' || cleaned[field] === null) {
      cleaned[field] = null;
    }
  });

  return cleaned;
}

export const propertyService = {
  // Fetch all properties
  async getAllProperties() {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching properties:', error);
      throw error;
    }
  },

  // Fetch single property with images
  async getPropertyById(id) {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select(`
          *,
          property_images (id, image_url, alt_text_en, alt_text_ar, display_order)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching property:', error);
      throw error;
    }
  },

  // Create property
  async createProperty(propertyData, userId) {
    try {
      const cleaned = cleanPropertyData(propertyData);
      const { data, error } = await supabase
        .from('properties')
        .insert({
          ...cleaned,
          created_by: userId,
          updated_by: userId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating property:', error);
      throw error;
    }
  },

  // Update property
  async updateProperty(id, propertyData, userId) {
    try {
      const cleaned = cleanPropertyData(propertyData);
      const { data, error } = await supabase
        .from('properties')
        .update({
          ...cleaned,
          updated_by: userId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating property:', error);
      throw error;
    }
  },

  // Delete property (admin only)
  async deleteProperty(id) {
    try {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting property:', error);
      throw error;
    }
  },

  // Upload property image
  async uploadPropertyImage(propertyId, file, altTextEn = '', altTextAr = '') {
    try {
      const fileName = `${propertyId}/${Date.now()}-${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from('property-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('property-images')
        .getPublicUrl(fileName);

      // Save image reference to database
      const { data: imageRecord, error: dbError } = await supabase
        .from('property_images')
        .insert({
          property_id: propertyId,
          image_url: publicUrl,
          alt_text_en: altTextEn,
          alt_text_ar: altTextAr,
        })
        .select()
        .single();

      if (dbError) throw dbError;
      return imageRecord;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  // Delete property image
  async deletePropertyImage(imageId, imagePath) {
    try {
      // Delete from storage
      await supabase.storage
        .from('property-images')
        .remove([imagePath]);

      // Delete from database
      const { error } = await supabase
        .from('property_images')
        .delete()
        .eq('id', imageId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  },

  // Batch update property status
  async updatePropertiesStatus(propertyIds, newStatus) {
    try {
      const { data, error } = await supabase
        .from('properties')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .in('id', propertyIds)
        .select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating properties status:', error);
      throw error;
    }
  },
};

// User/Role Services
export const userService = {
  // Fetch all users with roles
  async getAllUsers() {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('id, user_id, role, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Update user role (admin only)
  async updateUserRole(userId, newRole) {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .update({ role: newRole })
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  },

  // Get current user's role
  async getCurrentUserRole(userId) {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      return data?.role || null;
    } catch (error) {
      console.error('Error fetching user role:', error);
      return null;
    }
  },
};
